import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Shield, LockSimple, WarningCircle, CheckCircle, Plus, UploadSimple, DownloadSimple, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Same lightweight session-scoped passcode gate as SapWritePage.js - this
// creates a real, permanent Material master record in live SAP.
const PASSCODE = "admin";
const SESSION_KEY = "create_material_unlocked";

const COMMON_UOM_CODES = ["EA", "KGM", "MTR", "LTR", "PC", "SET", "BOX", "TO"];

const inputCls =
  "h-9 px-3 text-sm rounded-sm border border-[#D0D5DD] text-[#101828] focus:outline-none focus:border-[#004B87] focus:ring-1 focus:ring-[#004B87] w-full";

export default function CreateMaterialPage() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(SESSION_KEY) === "true");
  const [passcodeInput, setPasscodeInput] = useState("");
  const [passcodeError, setPasscodeError] = useState(null);

  const [materialId, setMaterialId] = useState("");
  const [productCategoryId, setProductCategoryId] = useState("");
  const [baseUom, setBaseUom] = useState("EA");
  const [description, setDescription] = useState("");
  // Feb 2026, user's explicit ask - "create additional name same as
  // product name in the master...add this column writable". Mirrors
  // Description as the user types, UNLESS they've manually edited this
  // field themselves (then it stops auto-following) - same "linked until
  // touched" pattern as most "same as X, but editable" form fields.
  const [additionalName, setAdditionalName] = useState("");
  const additionalNameTouchedRef = useRef(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [created, setCreated] = useState(null);
  const [undoing, setUndoing] = useState(false);

  // Feb 2026, user's explicit ask #1 & #2 - a real category dropdown
  // (SAP has no live "list all categories" service on this tenant, so
  // this list comes from a small fixed catalog the user supplied) and
  // an "activate at site(s)" checkbox set that runs creation + full
  // master-data activation in the same click.
  const [categories, setCategories] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);

  // Feb 2026, user's explicit ask #3-#5 - bulk create from an Excel
  // file, with a downloadable template and a mandatory confirm-before-
  // write preview step.
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreviewRows, setBulkPreviewRows] = useState(null);
  const [bulkPreviewOpen, setBulkPreviewOpen] = useState(false);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkExecuting, setBulkExecuting] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const bulkFileInputRef = useRef(null);

  const [sitesLoadError, setSitesLoadError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/material-categories`).then(({ data }) => setCategories(data.categories || [])).catch(() => {});
    axios.get(`${API}/purchase-orders/sites`).then(({ data }) => setSites(data.sites || [])).catch(() => setSitesLoadError(true));
  }, []);

  const allSitesSelected = sites.length > 0 && selectedSiteIds.length === sites.length;
  const toggleAllSites = (checked) => setSelectedSiteIds(checked ? [...sites] : []);
  const toggleSite = (siteId, checked) =>
    setSelectedSiteIds((prev) => (checked ? [...prev, siteId] : prev.filter((s) => s !== siteId)));

  const handleBulkFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setBulkFile(file);
    setBulkResults(null);
  };

  const previewBulkFile = async () => {
    if (!bulkFile) return;
    setBulkPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", bulkFile);
      const { data } = await axios.post(`${API}/admin/create-material/bulk-preview`, formData);
      setBulkPreviewRows(data.rows || []);
      setBulkPreviewOpen(true);
    } catch (err) {
      toast.error("Could not read this file", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const [bulkProgress, setBulkProgress] = useState(null); // {completed, total}

  const hasBulkErrors = (bulkPreviewRows || []).some((r) => r.errors?.length);

  const confirmBulkExecute = async () => {
    if (!bulkFile || hasBulkErrors) return;
    setBulkExecuting(true);
    setBulkProgress(null);
    try {
      const formData = new FormData();
      formData.append("file", bulkFile);
      const { data: startData } = await axios.post(`${API}/admin/create-material/bulk-execute`, formData);
      const jobId = startData.job_id;
      // Feb 2026 - bulk writes can take minutes (each row is several real
      // SAP SOAP calls); poll a job_id instead of waiting on one long
      // request, which would otherwise be cut off by the ingress timeout
      // while SAP writes kept happening in the background (testing_agent
      // finding).
      let job;
      const pollStartedAt = Date.now();
      const MAX_POLL_MS = 15 * 60 * 1000; // 15 minutes
      for (;;) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data } = await axios.get(`${API}/admin/create-material/bulk-status/${jobId}`);
        job = data;
        setBulkProgress({ completed: job.completed || 0, total: job.total || 0 });
        if (job.status === "done" || job.status === "failed") break;
        if (Date.now() - pollStartedAt > MAX_POLL_MS) {
          job = { status: "failed", error: "Still running after 15 minutes - check back later, it may still finish in the background." };
          break;
        }
      }
      if (job.status === "failed") {
        toast.error("Bulk creation failed", { description: job.error || "Unknown error" });
      } else {
        setBulkResults(job.results || []);
        setBulkPreviewOpen(false);
        const results = job.results || [];
        const createdCount = results.filter((r) => r.status === "created").length;
        const updatedCount = results.filter((r) => r.status === "updated").length;
        const partialCount = results.filter((r) => r.status === "partial").length;
        const failedCount = results.filter((r) => r.status === "failed").length;
        const parts = [];
        if (createdCount) parts.push(`${createdCount} created`);
        if (updatedCount) parts.push(`${updatedCount} updated`);
        if (partialCount) parts.push(`${partialCount} partial`);
        if (failedCount) parts.push(`${failedCount} failed`);
        toast.success(`Bulk run finished`, { description: parts.join(", ") || `${results.length} row(s) processed` });
      }
    } catch (err) {
      toast.error("Bulk creation failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBulkExecuting(false);
      setBulkProgress(null);
    }
  };

  const unlock = (e) => {
    e.preventDefault();
    if (passcodeInput === PASSCODE) {
      sessionStorage.setItem(SESSION_KEY, "true");
      setUnlocked(true);
      setPasscodeError(null);
    } else {
      setPasscodeError("Incorrect passcode");
    }
  };

  const canSubmit = materialId.trim() && productCategoryId.trim() && baseUom.trim() && description.trim() && additionalName.length <= 40 && !creating;

  // Feb 2026, user's explicit ask - SAP only allows 40 chars for Description.
  // If the typed name fits in 40 chars, Additional Name mirrors it as before.
  // If it overflows 40 chars, Description is capped to the first 40 chars and
  // the remainder (the part that didn't fit) is pushed into Additional Name,
  // so the full name is still captured across both fields instead of being
  // silently truncated.
  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= 40) {
      setDescription(value);
      if (!additionalNameTouchedRef.current) setAdditionalName(value);
    } else {
      const capped = value.slice(0, 40);
      const overflow = value.slice(40);
      setDescription(capped);
      if (!additionalNameTouchedRef.current) setAdditionalName(overflow);
    }
  };

  const handleAdditionalNameChange = (e) => {
    additionalNameTouchedRef.current = true;
    setAdditionalName(e.target.value);
  };

  const createMaterial = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setCreating(true);
    setCreateError(null);
    setCreated(null);
    try {
      const { data } = await axios.post(`${API}/admin/create-material`, {
        material_id: materialId.trim(),
        product_category_id: productCategoryId.trim(),
        base_uom: baseUom.trim(),
        description: description.trim(),
        additional_name: additionalName.trim() || null,
        site_ids: selectedSiteIds,
      });
      setCreated(data);
      toast.success("Material created in SAP", { description: `${data.material_id} (${data.uuid})${selectedSiteIds.length ? ` - activated at ${selectedSiteIds.length} site(s)` : ""}` });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Failed to create material";
      setCreateError(detail);
      toast.error("SAP rejected the material", { description: detail });
    } finally {
      setCreating(false);
    }
  };

  const undoCreate = async () => {
    if (!created) return;
    setUndoing(true);
    try {
      await axios.post(`${API}/admin/delete-material`, { material_id: created.material_id });
      toast.success("Deleted from SAP", { description: `${created.material_id} removed - it was still In Preparation` });
      setCreated(null);
      setMaterialId("");
      setProductCategoryId("");
      setDescription("");
      setAdditionalName("");
      additionalNameTouchedRef.current = false;
    } catch (err) {
      toast.error("Could not delete", { description: err?.response?.data?.detail || err.message });
    } finally {
      setUndoing(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]">
        <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
          <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
              <Shield size={18} weight="fill" className="text-white" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
              <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Create Material</span>
            </div>
          </div>
          <div className="w-px h-7 bg-white/25 shrink-0" />
          <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
            <NavTabs />
          </div>
          <SapConnectionStatus />
          <ErpConnectionStatus />
        </header>
        <main className="flex-1 flex items-center justify-center">
          <form
            onSubmit={unlock}
            className="bg-white border border-[#D0D5DD] rounded-sm p-6 w-full max-w-sm flex flex-col items-center gap-3 shadow-[0_1px_3px_0_rgba(16,24,40,0.1)]"
            data-testid="create-material-passcode-form"
          >
            <div className="w-10 h-10 rounded-full bg-[#FFFAEB] flex items-center justify-center">
              <LockSimple size={18} weight="bold" className="text-[#B54708]" />
            </div>
            <h1 className="font-heading text-sm font-bold text-[#1D2939]">Restricted Area</h1>
            <p className="text-[13px] text-[#475467] text-center">
              This page creates a new, permanent Material record in live SAP. Enter the admin passcode to continue.
            </p>
            <input
              type="password"
              autoFocus
              placeholder="Passcode"
              value={passcodeInput}
              onChange={(e) => { setPasscodeInput(e.target.value); setPasscodeError(null); }}
              className={inputCls}
              data-testid="create-material-passcode-input"
            />
            {passcodeError && (
              <p className="text-xs text-[#B42318]" data-testid="create-material-passcode-error">{passcodeError}</p>
            )}
            <Button type="submit" className="h-9 w-full bg-[#004B87] hover:bg-[#003A6A] text-white text-sm rounded-sm" data-testid="create-material-passcode-submit">
              Unlock
            </Button>
          </form>
        </main>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Create Material</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <div className="shrink-0 w-8" />
      </header>

      <main className="flex-1 overflow-auto p-4 space-y-4 max-w-xl mx-auto w-full">
        <div className="bg-[#FFFAEB] border border-[#FEDF89] rounded-sm p-3 flex items-start gap-2.5" data-testid="create-material-warning-banner">
          <WarningCircle size={16} weight="fill" className="text-[#B54708] mt-0.5 shrink-0" />
          <div className="text-[13px] text-[#7A4504]">
            <span className="font-bold">Careful: </span>
            this creates a real, permanent Material ID in SAP (checked first that the ID doesn't already exist).
            It's created "In Preparation" only - not yet usable for purchasing, sales, or production until someone
            activates it further in SAP. Ask your SAP admin for the correct Product Category code for this material type.
          </div>
        </div>

        <form onSubmit={createMaterial} className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3" data-testid="create-material-form">
          <div>
            <Label className="text-xs font-bold text-[#344054]">Material ID</Label>
            <input
              value={materialId}
              onChange={(e) => setMaterialId(e.target.value)}
              placeholder="e.g. RAW-STEEL-05"
              className={inputCls}
              data-testid="create-material-id-input"
            />
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Product Category</Label>
            <Select value={productCategoryId} onValueChange={setProductCategoryId}>
              <SelectTrigger data-testid="create-material-category-select-trigger">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`create-material-category-option-${c.code}`}>
                    {c.code} - {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Base UoM</Label>
            <Select value={baseUom} onValueChange={setBaseUom}>
              <SelectTrigger data-testid="create-material-uom-select-trigger">
                <SelectValue placeholder="EA" />
              </SelectTrigger>
              <SelectContent>
                {COMMON_UOM_CODES.map((code) => (
                  <SelectItem key={code} value={code} data-testid={`create-material-uom-option-${code}`}>{code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold text-[#344054]">Description (English)</Label>
              <span className={`text-[10px] ${description.length > 40 ? "text-[#B42318]" : "text-[#98A2B3]"}`} data-testid="create-material-description-counter">{description.length}/40</span>
            </div>
            <input
              value={description}
              onChange={handleDescriptionChange}
              placeholder="e.g. Steel Rod 5mm"
              className={inputCls}
              data-testid="create-material-description-input"
            />
            {description.length === 40 && (
              <p className="mt-1 text-[11px] text-[#B54708]" data-testid="create-material-description-overflow-hint">
                SAP allows 40 characters max. Anything beyond that was moved into Additional Name below - check or edit it.
              </p>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold text-[#344054]">Additional Name</Label>
              <span className={`text-[10px] ${additionalName.length > 40 ? "text-[#B42318]" : "text-[#98A2B3]"}`} data-testid="create-material-additional-name-counter">{additionalName.length}/40</span>
            </div>
            <input
              value={additionalName}
              onChange={handleAdditionalNameChange}
              placeholder="Defaults to the Description above"
              className={inputCls}
              data-testid="create-material-additional-name-input"
            />
            {additionalName.length > 40 ? (
              <p className="text-[11px] text-[#B42318] mt-1" data-testid="create-material-additional-name-error">Shorten this to 40 characters or less before submitting - SAP will reject it otherwise.</p>
            ) : (
              <p className="text-[11px] text-[#667085] mt-1">Mirrors Description unless you type a different value here. SAP allows 40 characters max.</p>
            )}
          </div>
          {createError && (
            <p className="text-xs text-[#B42318]" data-testid="create-material-error">{createError}</p>
          )}
          <div className="border-t border-[#EAECF0] pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-bold text-[#344054]">Activate at site(s) (optional)</Label>
              <label className="flex items-center gap-1.5 text-xs text-[#475467] cursor-pointer" data-testid="create-material-select-all-sites-label">
                <Checkbox
                  checked={allSitesSelected}
                  onCheckedChange={toggleAllSites}
                  data-testid="create-material-select-all-sites-checkbox"
                />
                All sites
              </label>
            </div>
            <div className="grid grid-cols-3 gap-1.5 bg-[#F9FAFB] border border-[#EAECF0] rounded-sm p-2 max-h-32 overflow-auto">
              {sites.map((s) => (
                <label key={s} className="flex items-center gap-1.5 text-xs text-[#1D2939] cursor-pointer" data-testid={`create-material-site-label-${s}`}>
                  <Checkbox
                    checked={selectedSiteIds.includes(s)}
                    onCheckedChange={(checked) => toggleSite(s, checked)}
                    data-testid={`create-material-site-checkbox-${s}`}
                  />
                  {s}
                </label>
              ))}
              {sitesLoadError && (
                <p className="col-span-3 text-xs text-[#B42318]" data-testid="create-material-sites-load-error">
                  Could not load the site list - site activation is unavailable right now.
                </p>
              )}
              {!sitesLoadError && sites.length === 0 && (
                <p className="col-span-3 text-xs text-[#475467]" data-testid="create-material-sites-loading">
                  Loading sites...
                </p>
              )}
            </div>
            {selectedSiteIds.length > 0 && (
              <p className="text-[11px] text-[#475467] mt-1.5" data-testid="create-material-site-activation-note">
                Planning, logistics & availability will be activated at {selectedSiteIds.length} site(s) right after
                creation (Valuation/cost is skipped here - use the separate Activate Material at Site page for that).
                {["RM", "CONSUMABLES"].includes(productCategoryId) && " Purchasing will also be activated since this category requires it."}
              </p>
            )}
          </div>
          <Button
            type="submit"
            disabled={!canSubmit}
            className="h-9 w-full bg-[#004B87] hover:bg-[#003A6A] text-white text-sm rounded-sm flex items-center justify-center gap-1.5"
            data-testid="create-material-submit-button"
          >
            <Plus size={14} weight="bold" />
            {creating ? "Creating in SAP..." : "Create Material in SAP"}
          </Button>
        </form>

        {created && (
          <div className="bg-[#ECFDF3] border border-[#ABEFC6] rounded-sm p-4 space-y-2" data-testid="create-material-success-card">
            <div className="flex items-center gap-2 text-[#027A48] font-bold text-sm">
              <CheckCircle size={16} weight="fill" />
              Created in SAP
            </div>
            <div className="text-[13px] text-[#065F46]">
              Material ID: <span className="font-mono font-bold" data-testid="created-material-id">{created.material_id}</span>
            </div>
            <div className="text-[13px] text-[#065F46]">
              UUID: <span className="font-mono" data-testid="created-material-uuid">{created.uuid}</span>
            </div>
            {created.site_activation && Object.keys(created.site_activation).length > 0 && (
              <div className="text-[12px] text-[#065F46] bg-white/60 border border-[#ABEFC6] rounded-sm p-2 space-y-1" data-testid="create-material-site-activation-result">
                {Object.entries(created.site_activation).filter(([k]) => k !== "_purchasing").map(([siteId, r]) => (
                  <div key={siteId}>
                    <span className="font-bold">{siteId}:</span> planning/logistics {String(r.planning_logistics?.status || r.planning_logistics || "")}, valuation {String(r.valuation || "")}
                  </div>
                ))}
                {created.site_activation._purchasing && (
                  <div><span className="font-bold">Purchasing:</span> {String(created.site_activation._purchasing)}</div>
                )}
              </div>
            )}
            <Button
              variant="outline"
              onClick={undoCreate}
              disabled={undoing}
              className="h-8 text-xs border-[#FECDCA] text-[#B42318] hover:bg-[#FEF3F2]"
              data-testid="create-material-undo-button"
            >
              {undoing ? "Deleting..." : "Undo (delete this material)"}
            </Button>
          </div>
        )}

        <div className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3" data-testid="create-material-bulk-section">
          <div>
            <h2 className="font-heading text-sm font-bold text-[#1D2939]">Bulk Create from Excel</h2>
            <p className="text-[12px] text-[#475467] mt-0.5">
              Create many materials at once. Download the template, fill in a row per material, then upload it below.
              Nothing is written to SAP until you review and confirm.
            </p>
          </div>
          <a
            href={`${API}/admin/create-material/bulk-template`}
            className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-bold text-[#004B87] border border-[#D0D5DD] rounded-sm hover:bg-[#F9FAFB] w-fit"
            data-testid="create-material-bulk-template-link"
          >
            <DownloadSimple size={14} weight="bold" />
            Download Excel template
          </a>
          <div className="flex items-center gap-2">
            <input
              ref={bulkFileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleBulkFileChange}
              className="text-xs flex-1"
              data-testid="create-material-bulk-file-input"
            />
            <Button
              type="button"
              onClick={previewBulkFile}
              disabled={!bulkFile || bulkPreviewLoading}
              className="h-8 text-xs bg-[#004B87] hover:bg-[#003A6A] text-white rounded-sm flex items-center gap-1.5 shrink-0"
              data-testid="create-material-bulk-preview-button"
            >
              <UploadSimple size={14} weight="bold" />
              {bulkPreviewLoading ? "Reading..." : "Preview"}
            </Button>
          </div>

          {bulkResults && (
            <div className="border border-[#EAECF0] rounded-sm overflow-auto max-h-56" data-testid="create-material-bulk-results">
              <table className="w-full text-[12px]">
                <thead className="bg-[#F9FAFB] sticky top-0">
                  <tr>
                    <th className="text-left p-1.5 font-bold text-[#344054]">Material ID</th>
                    <th className="text-left p-1.5 font-bold text-[#344054]">Status</th>
                    <th className="text-left p-1.5 font-bold text-[#344054]">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkResults.map((r, i) => (
                    <tr key={i} className="border-t border-[#EAECF0]" data-testid={`create-material-bulk-result-row-${i}`}>
                      <td className="p-1.5 font-mono">{r.material_id}</td>
                      <td className={`p-1.5 font-bold ${["created", "updated"].includes(r.status) ? "text-[#027A48]" : r.status === "partial" ? "text-[#B54708]" : r.status === "skipped" ? "text-[#B54708]" : "text-[#B42318]"}`}>
                        {["created", "updated"].includes(r.status) && <CheckCircle size={12} weight="fill" className="inline mr-1" />}
                        {r.status === "partial" && <WarningCircle size={12} weight="fill" className="inline mr-1" />}
                        {r.status === "failed" && <XCircle size={12} weight="fill" className="inline mr-1" />}
                        {r.status}
                      </td>
                      <td className="p-1.5 text-[#475467]">{r.detail || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={bulkPreviewOpen} onOpenChange={setBulkPreviewOpen}>
          <DialogContent className="max-w-2xl" data-testid="create-material-bulk-preview-dialog">
            <DialogHeader>
              <DialogTitle>Confirm create / update in SAP</DialogTitle>
              <DialogDescription>
                {hasBulkErrors
                  ? "Some rows have errors and must be fixed in the Excel file before anything can be created."
                  : `${bulkPreviewRows?.length || 0} material(s) checked - new IDs are created, existing IDs have their real Description and Product Category overwritten in SAP - each activated at the site(s) listed in its row.`}
              </DialogDescription>
            </DialogHeader>
            <div className="border border-[#EAECF0] rounded-sm overflow-auto max-h-80">
              <table className="w-full text-[12px]">
                <thead className="bg-[#F9FAFB] sticky top-0">
                  <tr>
                    <th className="text-left p-1.5 font-bold text-[#344054]">Row</th>
                    <th className="text-left p-1.5 font-bold text-[#344054]">Material ID</th>
                    <th className="text-left p-1.5 font-bold text-[#344054]">In SAP?</th>
                    <th className="text-left p-1.5 font-bold text-[#344054]">Description</th>
                    <th className="text-left p-1.5 font-bold text-[#344054]">Additional Name</th>
                    <th className="text-left p-1.5 font-bold text-[#344054]">Category</th>
                    <th className="text-left p-1.5 font-bold text-[#344054]">UoM</th>
                    <th className="text-left p-1.5 font-bold text-[#344054]">Sites</th>
                    <th className="text-left p-1.5 font-bold text-[#344054]">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {(bulkPreviewRows || []).map((r) => (
                    <tr key={r.row_number} className={`border-t border-[#EAECF0] ${r.errors?.length ? "bg-[#FEF3F2]" : ""}`} data-testid={`create-material-bulk-preview-row-${r.row_number}`}>
                      <td className="p-1.5">{r.row_number}</td>
                      <td className="p-1.5 font-mono">{r.material_id}</td>
                      <td className="p-1.5" data-testid={`create-material-bulk-preview-exists-${r.row_number}`}>
                        {r.already_exists ? (
                          <span className="text-[#B54708] font-semibold">Existing - will update</span>
                        ) : (
                          <span className="text-[#475467]">New</span>
                        )}
                      </td>
                      <td className="p-1.5">{r.description}</td>
                      <td className="p-1.5 text-[#475467]">{r.additional_name || "(same as Description)"}</td>
                      <td className="p-1.5">{r.product_category_id}</td>
                      <td className="p-1.5">{r.base_uom}</td>
                      <td className="p-1.5">{(r.site_ids || []).join(", ")}</td>
                      <td className="p-1.5 text-[#B42318]">{(r.errors || []).join("; ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {bulkProgress && (
              <p className="text-[12px] text-[#475467]" data-testid="create-material-bulk-progress">
                Creating in SAP: {bulkProgress.completed} of {bulkProgress.total} done...
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setBulkPreviewOpen(false)} className="h-8 text-xs" data-testid="create-material-bulk-cancel-button">
                Cancel
              </Button>
              <Button
                onClick={confirmBulkExecute}
                disabled={hasBulkErrors || bulkExecuting}
                className="h-8 text-xs bg-[#004B87] hover:bg-[#003A6A] text-white"
                data-testid="create-material-bulk-confirm-button"
              >
                {bulkExecuting ? "Running in SAP..." : "Confirm & run in SAP"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
